import type { StepViewProps } from './flow-route-types.js';
import { firstIncompleteStep } from './gating.js';
import { fulfillmentMethodLabels, formatDateTime, labelOrDash, textOrDash } from './format.js';
import { Notice } from './notice.jsx';
import { useOnboarding } from './use-onboarding.jsx';
import { WhatsappLink } from './step-activate.jsx';

export function StepDashboard({ steps, onNavigate }: StepViewProps) {
  const { draft, connection, store } = useOnboarding();
  const active = connection?.status === 'activated';

  const billingStatus = effectiveBillingStatus(store?.billingStatus ?? null, store?.trialEndsAt ?? null);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <StatusBadge active={active} billingStatus={billingStatus} />
      </div>

      <TrialPanel active={active} trialEndsAt={store?.trialEndsAt ?? null} billingStatus={billingStatus} />
      <InactivePanel active={active} steps={steps} onNavigate={onNavigate} />

      <div className="grid gap-4 sm:grid-cols-3">
        <Card
          title="Mercado"
          value={textOrDash(store?.name ?? draft.store.name)}
          hint={textOrDash(draft.store.formattedAddress)}
        />
        <Card
          title="Atendimento"
          value={schedulesResumo(draft.service.schedules)}
          hint={`${paymentMethodsResumo(draft.service.paymentMethods)} · ${fulfillmentMethodsResumo(draft.service.fulfillmentMethods)}`}
        />
        <Card
          title="WhatsApp"
          value={textOrDash(connection?.displayPhoneNumber ?? store?.phoneNumber ?? '')}
          hint={textOrDash(connection?.verifiedName ?? '')}
        />
      </div>

      <ActivePanel active={active} displayPhoneNumber={connection?.displayPhoneNumber ?? store?.phoneNumber ?? null} />
    </div>
  );
}

function effectiveBillingStatus(billingStatus: string | null, trialEndsAt: string | null): string | null {
  if (billingStatus !== 'trialing') {
    return billingStatus;
  }

  if (!trialEndsAt) {
    return billingStatus;
  }

  const end = new Date(trialEndsAt).getTime();

  if (Number.isNaN(end)) {
    return billingStatus;
  }

  if (end >= Date.now()) {
    return billingStatus;
  }

  return 'trial_expired';
}

function StatusBadge({ active, billingStatus }: { active: boolean; billingStatus: string | null }) {
  if (!active) {
    return <span className="badge badge-warning">Configuração incompleta</span>;
  }

  if (billingStatus === 'trial_expired') {
    return <span className="badge badge-error">Teste expirado</span>;
  }

  if (billingStatus === 'paid') {
    return <span className="badge badge-success">Plano ativo</span>;
  }

  return <span className="badge badge-success">Teste grátis ativo</span>;
}

function TrialPanel({
  active,
  trialEndsAt,
  billingStatus,
}: {
  active: boolean;
  trialEndsAt: string | null;
  billingStatus: string | null;
}) {
  if (!active) {
    return null;
  }

  if (billingStatus === 'trial_expired') {
    return (
      <Notice tone="warning" title="Seu teste grátis terminou">
        Fale com a Marketside para escolher um plano e continuar atendendo pelo WhatsApp.
      </Notice>
    );
  }

  if (billingStatus === 'paid') {
    return (
      <Notice tone="success" title="Plano ativo">
        Sua loja já está em um plano ativo da Marketside.
      </Notice>
    );
  }

  return (
    <Notice tone="success" title="Teste grátis de 7 dias ativo">
      {trialMessage(trialEndsAt)}
    </Notice>
  );
}

function trialMessage(trialEndsAt: string | null): string {
  if (!trialEndsAt) {
    return 'Seu teste grátis começou. Você tem 7 dias para usar a Marketside sem pagar nada.';
  }

  return `Você ainda tem ${contagem(daysRestantes(trialEndsAt), 'dia', 'dias')} de teste. Ele termina em ${formatDateTime(trialEndsAt)}.`;
}

function InactivePanel({
  active,
  steps,
  onNavigate,
}: {
  active: boolean;
  steps: StepViewProps['steps'];
  onNavigate: (path: string) => void;
}) {
  const { draft, connection } = useOnboarding();

  if (active) {
    return null;
  }

  const next = firstIncompleteStep(steps, { draft, connection });

  // Deliberately an explanation rather than a redirect: bouncing someone out of a bookmarked
  // /dashboard is more annoying than showing them what is left.
  return (
    <Notice tone="info" title="Seu teste grátis ainda não começou">
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <span>Ainda faltam etapas dos primeiros passos.</span>
        <ContinueButton next={next} onNavigate={onNavigate} />
      </div>
    </Notice>
  );
}

function ContinueButton({
  next,
  onNavigate,
}: {
  next: ReturnType<typeof firstIncompleteStep>;
  onNavigate: (path: string) => void;
}) {
  if (!next) {
    return null;
  }

  return (
    <button className="btn btn-primary btn-xs" type="button" onClick={() => onNavigate(next.path)}>
      Continuar configuração
    </button>
  );
}

function ActivePanel({
  active,
  displayPhoneNumber,
}: {
  active: boolean;
  displayPhoneNumber: string | null;
}) {
  if (!active) {
    return null;
  }

  return <WhatsappLink displayPhoneNumber={displayPhoneNumber} />;
}

function schedulesResumo(schedules: Array<{ isOpen: boolean }>): string {
  const daysAbertos = schedules.filter((schedule) => schedule.isOpen).length;

  if (daysAbertos === 0) {
    return '—';
  }

  return `Aberto ${contagem(daysAbertos, 'dia', 'dias')} por semana`;
}

function paymentMethodsResumo(paymentMethods: string[]): string {
  if (paymentMethods.length === 0) {
    return 'sem forma de pagamento';
  }

  return contagem(paymentMethods.length, 'forma de pagamento', 'formas de pagamento');
}

function contagem(total: number, singular: string, plural: string): string {
  if (total === 1) {
    return `1 ${singular}`;
  }

  return `${total} ${plural}`;
}

function fulfillmentMethodsResumo(fulfillmentMethods: string[]): string {
  if (fulfillmentMethods.length === 0) {
    return 'sem entrega definida';
  }

  return fulfillmentMethods.map((entrega) => labelOrDash(fulfillmentMethodLabels, entrega)).join(', ');
}

function daysRestantes(trialEndsAt: string): number {
  const end = new Date(trialEndsAt).getTime();

  if (Number.isNaN(end)) {
    return 0;
  }

  const ms = end - Date.now();

  return Math.max(0, Math.ceil(ms / 86_400_000));
}

function Card({ title, value, hint }: { title: string; value: string; hint: string }) {
  return (
    <div className="rounded-box border border-base-300 p-4">
      <p className="text-xs uppercase tracking-wide opacity-60">{title}</p>
      <p className="mt-1 font-medium">{value}</p>
      <p className="text-sm opacity-60">{hint}</p>
    </div>
  );
}
